import {
  Alert,
  Box,
  Container,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useBoxOccupancy, useLowSpaceAlerts, useRemainingSpace, useStatistics } from '../hooks/useApi';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';
import BoxGrid, { BoxGridLegend } from '../components/BoxGrid';
import { BOX_CAPACITY } from '../config';

function SectionCard({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  return (
    <Box
      className="glass-card"
      sx={{
        flex: '1 1 340px',
        minWidth: 320,
        p: 0,
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 2.5,
          py: 1.75,
          borderBottom: '1px solid var(--color-outline)',
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '9px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'var(--color-primary-container)',
            color: 'var(--color-on-primary-container)',
          }}
        >
          <i className={`fas ${icon}`} style={{ fontSize: '0.85rem' }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--color-fg)' }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }}>{children}</Box>
    </Box>
  );
}

function EmptyRow({ text }: { text: string }) {
  return (
    <Box sx={{ py: 4, textAlign: 'center', color: 'var(--color-muted)', fontSize: '0.85rem' }}>
      {text}
    </Box>
  );
}

function StatSkeletons() {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
      {[0, 1, 2, 3].map((i) => (
        <Box
          key={i}
          className="skeleton"
          sx={{ flex: '1 1 180px', minWidth: 170, height: 132, borderRadius: 'var(--radius-md)' }}
        />
      ))}
    </Box>
  );
}

export default function DashboardPage() {
  const stats = useStatistics();
  const alerts = useLowSpaceAlerts();
  const space = useRemainingSpace();
  const [selectedBox, setSelectedBox] = useState<string | null>(null);
  const boxes = space.data ?? [];
  const activeBox = selectedBox ?? (boxes[0]?.box_number ?? null);
  const occupancy = useBoxOccupancy(activeBox);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <PageHeader
        title="Dashboard"
        subtitle="Live overview of your cryopreservation inventory and freezer-box capacity."
      />

      {alerts.data && alerts.data.length > 0 && (
        <Alert
          severity="warning"
          icon={<i className="fas fa-triangle-exclamation" />}
          sx={{ mb: 3, borderRadius: 'var(--radius-sm)', alignItems: 'center' }}
        >
          <strong>{alerts.data.length}</strong> box
          {alerts.data.length > 1 ? 'es are' : ' is'} at or near capacity:{' '}
          {alerts.data.map((a) => `Box ${a.box_number} (${a.remaining_space} free)`).join(', ')}
        </Alert>
      )}

      {stats.isLoading ? (
        <StatSkeletons />
      ) : stats.isError ? (
        <Alert severity="error" sx={{ borderRadius: 'var(--radius-sm)' }}>
          Failed to load statistics.
        </Alert>
      ) : stats.data ? (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
            <StatCard label="Records" value={stats.data.summary.total_records} icon="fa-vials" tone="primary" />
            <StatCard label="Total Vials" value={stats.data.summary.total_vials} icon="fa-flask" tone="sage" />
            <StatCard label="Total Cells" value={stats.data.summary.total_cells} icon="fa-dna" tone="accent" />
            <StatCard label="Unique Cell Lines" value={stats.data.summary.unique_cell_lines} icon="fa-layer-group" tone="primary" />
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <SectionCard title="By Origin" icon="fa-diagram-project">
              {stats.data.by_origin.length === 0 ? (
                <EmptyRow text="No records yet." />
              ) : (
                <TableContainer sx={{ border: 'none', borderRadius: 0, background: 'transparent' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Origin</TableCell>
                        <TableCell align="right">Records</TableCell>
                        <TableCell align="right">Vials</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.data.by_origin.map((g, i) => (
                        <TableRow key={i} hover>
                          <TableCell>{String((g as any).origin ?? 'Unspecified')}</TableCell>
                          <TableCell align="right">{g.count}</TableCell>
                          <TableCell align="right">{g.total_vials}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </SectionCard>

            <SectionCard title="Box Space" icon="fa-boxes-stacked">
              {(space.data ?? []).length === 0 ? (
                <EmptyRow text="No boxes in use yet." />
              ) : (
              <TableContainer sx={{ border: 'none', borderRadius: 0, background: 'transparent' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Box</TableCell>
                      <TableCell align="right">Used</TableCell>
                      <TableCell sx={{ minWidth: 150 }}>Remaining</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(space.data ?? []).map((b, i) => {
                      const used = Number(b.vials_used) || 0;
                      const pct = Math.min(100, Math.round((used / BOX_CAPACITY) * 100));
                      const near = pct >= 85;
                      const isActive = String(b.box_number) === String(activeBox);
                      return (
                        <TableRow
                          key={i}
                          hover
                          onClick={() => setSelectedBox(String(b.box_number))}
                          selected={isActive}
                          sx={{ cursor: 'pointer' }}>
                          <TableCell>{b.box_number}</TableCell>
                          <TableCell align="right">{b.vials_used}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                              <LinearProgress
                                variant="determinate"
                                value={pct}
                                sx={{
                                  flex: 1,
                                  height: 6,
                                  borderRadius: 99,
                                  bgcolor: 'var(--color-surface-3)',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 99,
                                    backgroundColor: near ? 'var(--color-accent)' : 'var(--color-primary)',
                                  },
                                }}
                              />
                              <Typography
                                className="font-mono"
                                sx={{ fontSize: '0.8rem', color: 'var(--color-muted)', minWidth: 24, textAlign: 'right' }}
                              >
                                {b.remaining_space}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
            </SectionCard>
          </Box>

          {boxes.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <SectionCard
                title={activeBox ? `Box ${activeBox} — Position Map` : 'Position Map'}
                icon="fa-table-cells"
              >
                <Box sx={{ p: 3 }}>
                  {occupancy.isLoading ? (
                    <Box className="skeleton" sx={{ height: 300, borderRadius: 'var(--radius-md)' }} />
                  ) : occupancy.data ? (
                    <>
                      <Typography
                        variant="body2"
                        sx={{ textAlign: 'center', color: 'var(--color-muted)', mb: 2 }}
                      >
                        {occupancy.data.used} of {occupancy.data.capacity} slots filled ·{' '}
                        {occupancy.data.remaining_space} free
                      </Typography>
                      <BoxGrid occupied={occupancy.data.occupied} />
                      <BoxGridLegend />
                    </>
                  ) : (
                    <EmptyRow text="Select a box to see its position map." />
                  )}
                </Box>
              </SectionCard>
            </Box>
          )}
        </>
      ) : null}
    </Container>
  );
}
